export interface RowCol {
	row: number;
	col: number;
}
export interface IndexSelectionWholeRowOrCol {
	from: number;
	to: number;
}
export interface IndexSelectionCells {
	from: RowCol;
	to: RowCol;
}
type IndexSelection = IndexSelectionWholeRowOrCol | IndexSelectionCells;
type A1Selection = string;

enum SelectionType {
	A1 = 'a1',
	IDX = 'idx',
}

class SheetSelection {
	private _selectionType: SelectionType;
	private _selection: A1Selection | IndexSelection;

	static cellRegex = new RegExp(
		'^([A-Z]|[A-Z][A-Z]|[A-Z][A-Z][A-Z])([1-9]|[1-9][0-9]|[1-9][0-9][0-9]|[1-9][0-9][0-9][0-9]|[1-9][0-9][0-9][0-9][0-9]|[1-9][0-9][0-9][0-9][0-9][0-9]|10[0-3][0-9][0-9][0-9][0-9]|104[0-7][0-9][0-9][0-9]|1048[0-4][0-9][0-9]|10485[0-6][0-9]|104857[0-6])$',
		'i',
	);
	static numberRegex = new RegExp(
		'^([1-9]|[1-9][0-9]|[1-9][0-9][0-9]|[1-9][0-9][0-9][0-9]|[1-9][0-9][0-9][0-9][0-9]|[1-9][0-9][0-9][0-9][0-9][0-9]|10[0-3][0-9][0-9][0-9][0-9]|104[0-7][0-9][0-9][0-9]|1048[0-4][0-9][0-9]|10485[0-6][0-9]|104857[0-6])$',
	);
	static letterRegex = new RegExp('^([A-Z]|[A-Z][A-Z]|[A-Z][A-Z][A-Z])$', 'i');

	a1Prefix!: string;
	isWholeRows!: boolean;
	isWholeCols!: boolean;
	isValid: boolean;
	isRange!: boolean;

	/**
	 * @param {string} selectionType - "a1"
	 * @param {string} selection - "F2:F10"
	 */
	constructor(selectionType: SelectionType, selection: A1Selection | IndexSelection) {
		this._selectionType = selectionType;
		this._selection = selection;
		this.isValid = true;
	}

	get valueA1(): A1Selection {
		if (this._selectionType === SelectionType.A1) {
			return this.a1Prefix + (this._selection as A1Selection);
		} else {
			const _selection = this._selection as IndexSelectionCells;

			if (this.isRange) {
				let from, to;

				if (this.isWholeRows) {
					from = SheetSelection.transformRowIndexToA1(_selection.from.row);
					to = SheetSelection.transformRowIndexToA1(_selection.to.row);
				} else if (this.isWholeCols) {
					from = SheetSelection.transformColumnIndexToA1(_selection.from.col);
					to = SheetSelection.transformColumnIndexToA1(_selection.to.col);
				} else {
					from = SheetSelection.transformCellIndexToA1(_selection.from);
					to = SheetSelection.transformCellIndexToA1(_selection.to);
				}

				return `${from}:${to}`;
			} else {
				return SheetSelection.transformCellIndexToA1(_selection.from);
			}
		}
	}

	get valueIndex(): IndexSelection | RowCol {
		if (this._selectionType === SelectionType.IDX) {
			return this._selection as IndexSelection;
		} else {
			const _selection = this._selection as A1Selection;

			if (this.isRange) {
				const parts = _selection.split(':');
				let corner1, corner2;

				if (this.isWholeRows) {
					corner1 = SheetSelection.transformRowA1ToIndex(parts[0]!);
					corner2 = SheetSelection.transformRowA1ToIndex(parts[1]!);

					return {
						from: Math.min(corner1, corner2),
						to: Math.max(corner1, corner2),
					};
				} else if (this.isWholeCols) {
					corner1 = SheetSelection.transformColumnA1ToIndex(parts[0]!);
					corner2 = SheetSelection.transformColumnA1ToIndex(parts[1]!);

					return {
						from: Math.min(corner1, corner2),
						to: Math.max(corner1, corner2),
					};
				} else {
					corner1 = SheetSelection.transformCellA1ToIndex(parts[0]!);
					corner2 = SheetSelection.transformCellA1ToIndex(parts[1]!);

					return {
						from: {
							col: Math.min(corner1.col, corner2.col),
							row: Math.min(corner1.row, corner2.row),
						},
						to: {
							col: Math.max(corner1.col, corner2.col),
							row: Math.max(corner1.row, corner2.row),
						},
					};
				}
			} else {
				return SheetSelection.transformCellA1ToIndex(_selection);
			}
		}
	}

	isCellIncludedInRange(cellA1: A1Selection): boolean {
		let isIncluded;
		const cellIndex = SheetSelection.fromA1(cellA1).valueIndex as RowCol,
			valueIndex = this.valueIndex;

		if (this.isWholeRows) {
			const rangeIndices = valueIndex as IndexSelectionWholeRowOrCol;
			isIncluded = cellIndex.row >= rangeIndices.from && cellIndex.row <= rangeIndices.to;
		} else if (this.isWholeCols) {
			const rangeIndices = valueIndex as IndexSelectionWholeRowOrCol;
			isIncluded = cellIndex.col >= rangeIndices.from && cellIndex.col <= rangeIndices.to;
		} else if (this.isRange) {
			const rangeIndices = valueIndex as IndexSelectionCells;
			isIncluded =
				cellIndex.col >= rangeIndices.from.col &&
				cellIndex.col <= rangeIndices.to.col &&
				cellIndex.row >= rangeIndices.from.row &&
				cellIndex.row <= rangeIndices.to.row;
		} else {
			const rangeIndices = valueIndex as RowCol;
			isIncluded = cellIndex.row === rangeIndices.row && cellIndex.col === rangeIndices.col;
		}

		return isIncluded;
	}

	// ---------- creating from selection

	static fromA1(selection: A1Selection): SheetSelection {
		// "A1:B1" or "A1", or "A:B", or "'Sheet One'!A1:B2"

		// splitting off the sheet name
		const sheetParts = (selection || '').split('!');
		selection = sheetParts[sheetParts.length - 1]!;

		const sheetSelection = new SheetSelection(SelectionType.A1, selection);

		sheetSelection.a1Prefix = sheetParts.length > 1 ? sheetParts[0] + '!' : '';

		const rangeParts = selection.split(':');

		if (rangeParts.length === 1) {
			// validate as cell
			const type1 = this.getA1PartType(rangeParts[0]!);

			if (type1 === 'cell') {
				sheetSelection.isRange = false;
				sheetSelection.isWholeRows = false;
				sheetSelection.isWholeCols = false;
			} else {
				sheetSelection.isValid = false;
			}
		} else if (rangeParts.length === 2) {
			sheetSelection.isRange = true;
			sheetSelection.isWholeRows = false;
			sheetSelection.isWholeCols = false;

			// check if full rows / columns are selected (e.g. 'A:A' or '2:3')
			const type1 = this.getA1PartType(rangeParts[0]!),
				type2 = this.getA1PartType(rangeParts[1]!);

			if (type1 === type2) {
				if (type1 === 'row' && type2 === 'row') {
					sheetSelection.isWholeRows = true;
				}

				if (type1 === 'col' && type2 === 'col') {
					sheetSelection.isWholeCols = true;
				}
			} else {
				sheetSelection.isValid = false;
			}
		} else {
			sheetSelection.isValid = false;
		}

		return sheetSelection;
	}

	static fromIndex(
		selection: IndexSelection,
		isWholeRows = false,
		isWholeCols = false,
	): SheetSelection {
		const sheetSelection = new SheetSelection(SelectionType.IDX, selection);

		sheetSelection.isRange = true;
		if (
			typeof selection.from !== 'number' &&
			typeof selection.to !== 'number' &&
			typeof selection.from.row === 'number' &&
			typeof selection.from.col === 'number' &&
			selection.from.row === selection.to.row &&
			selection.from.col === selection.to.col
		) {
			sheetSelection.isRange = false; // just one cell
		}

		sheetSelection.isWholeRows = isWholeRows;
		sheetSelection.isWholeCols = isWholeCols;

		return sheetSelection;
	}

	// ------------ utility functions

	static getA1PartType(part: A1Selection): string | null {
		let type = null;

		if (this.cellRegex.test(part)) {
			type = 'cell';
		} else if (this.numberRegex.test(part)) {
			type = 'row';
		} else if (this.letterRegex.test(part)) {
			type = 'col';
		}
		return type;
	}

	static transformRowIndexToA1(index: number): A1Selection {
		return String(index + 1);
	}

	static transformColumnIndexToA1(index: number): A1Selection {
		let temp,
			a1 = '';
		while (index >= 0) {
			temp = index % 26;
			a1 = String.fromCharCode(temp + 65) + a1;
			index = (index - temp - 1) / 26;
		}
		return a1;
	}

	static transformRowA1ToIndex(a1Row: A1Selection): number {
		return parseInt(a1Row) - 1;
	}

	static transformColumnA1ToIndex(a1Col: A1Selection): number {
		a1Col = a1Col.toUpperCase();
		const A = 'A'.charCodeAt(0);
		let number = a1Col.charCodeAt(a1Col.length - 1) - A;
		if (a1Col.length === 2) {
			number += 26 * (a1Col.charCodeAt(0) - A + 1);
		}
		return number;
	}

	static transformCellIndexToA1(obj: RowCol): A1Selection {
		return this.transformColumnIndexToA1(obj.col) + this.transformRowIndexToA1(obj.row);
	}

	static transformCellA1ToIndex(a1: A1Selection): RowCol {
		a1 = a1.toUpperCase();
		const a1Parts = a1.match(this.cellRegex);
		return {
			col: a1Parts ? this.transformColumnA1ToIndex(a1Parts[1]!) : 0,
			row: a1Parts ? this.transformRowA1ToIndex(a1Parts[2]!) : 0,
		};
	}
}

export default SheetSelection;
